import { match } from 'ts-pattern'

import type {
  ConditionalOperator,
  ConditionalProperty
} from 'Automations/nodeDefinitions/conditions'
// Imported from the leaf function-strings module (not the `conditions`
// barrel) to avoid a circular import: the barrel re-exports
// `getUpstreamConditionalProperties`, which transitively pulls the node
// definitions registry — including the start nodes that call this factory —
// back into the cycle.
import {
  arrayExcludes,
  arrayIncludes,
  customProperty,
  customPropertyOperatorId,
  isCustomPropertyOperatorId,
  readCustomPropertyValue
} from 'Automations/nodeDefinitions/conditions/helpers/functionStrings'
import {
  semanticTypes,
  type SemanticType
} from 'Automations/nodeDefinitions/semanticTypes'
import {
  formatCustomProperty,
  formatCustomerCount
} from '../../../switch/components/GroupEditor/inputs'
import { CustomerPropertyEditor } from './CustomerPropertyEditor'

const isBlank = (v: unknown) => v === undefined || v === null || v === ''

// Validator for the identity operators (INCLUDES / EXCLUDES): at least one
// customer must be selected.
const validateCustomerIdsValue = ({ value }: { value: unknown }) =>
  Array.isArray(value) && value.length > 0
    ? undefined
    : [
        {
          title: 'Incomplete condition',
          message: 'Customer required',
          metadata: { field: 'customerIds' as const }
        }
      ]

// Validator for a custom-property operator: the custom-property key lives in
// the operator id, so only the comparison value is required here.
const validateCustomPropertyValue = ({ value }: { value: unknown }) => {
  const { value: comparand } = readCustomPropertyValue(value)
  return isBlank(comparand)
    ? [
        {
          title: 'Incomplete condition',
          message: 'Value required',
          metadata: { field: 'value' as const }
        }
      ]
    : undefined
}

// Builds the operator for a single custom-property key. The key is baked into
// the function string and recovered from the operator id elsewhere; the
// operator label is the key itself so it reads naturally in the dropdown and
// the canvas preview.
const buildCustomPropertyOperator = (key: string): ConditionalOperator => ({
  label: key,
  valueSemanticType: semanticTypes.sequence.customer.customProperties,
  functionString: customProperty(key),
  formatPreviewValue: formatCustomProperty
})

const buildCustomPropertyOperators = (
  customPropertyKeys: string[]
): Record<string, ConditionalOperator> =>
  Object.fromEntries(
    customPropertyKeys.map(key => [
      customPropertyOperatorId(key),
      buildCustomPropertyOperator(key)
    ])
  )

/**
 * Factory for the compound CUSTOMER conditional property. Shared by start nodes
 * that expose a customer. `customerIdSemanticType` backs the identity operators
 * (Includes / Excludes); `customPropertyKeys` — fetched by the node's async
 * `providesConditionalProperties` — adds one equals/not-equals operator per
 * custom-property key, each reading `sequence.customer.customProperties`.
 */
export const customerConditionalProperty = ({
  customerIdSemanticType,
  customPropertyKeys
}: {
  customerIdSemanticType: SemanticType
  customPropertyKeys: string[]
}): ConditionalProperty => ({
  label: 'Customer',
  // The property editor renders its own operator dropdown, so the framework's
  // auto-open would target a dropdown that isn't shown.
  focusOperatorOnSelect: false,
  propertyEditorComponent: CustomerPropertyEditor,
  // A group can hold multiple CUSTOMER rows (e.g. comparing two different
  // custom properties, or an identity check plus a custom-property check).
  propertyAvailable: () => true,
  validateProperty: ({ operator, value }) =>
    match(operator)
      .with('INCLUDES', 'EXCLUDES', () => validateCustomerIdsValue({ value }))
      .when(isCustomPropertyOperatorId, () =>
        validateCustomPropertyValue({ value })
      )
      .otherwise(() => undefined),
  operators: {
    INCLUDES: {
      label: 'Includes',
      valueSemanticType: customerIdSemanticType,
      functionString: arrayIncludes,
      formatPreviewValue: formatCustomerCount
    },
    EXCLUDES: {
      label: 'Excludes',
      valueSemanticType: customerIdSemanticType,
      functionString: arrayExcludes,
      formatPreviewValue: formatCustomerCount
    },
    ...buildCustomPropertyOperators(customPropertyKeys)
  }
})
